import type { ConstValues } from '@openx/types/helpers';
import type { LookalikeFormSegment, SimilarityLevelValue } from '../../../../../../types';

export type LookalikeDefinitionSegments = {
  similaritySegment?: LookalikeFormSegment;
  seedSegment?: LookalikeFormSegment;
};

export type LookalikeSegmentsState = {
  segmentsByDefinition: Record<string, LookalikeDefinitionSegments>;
  definitionOrder: string[];
};

export const ActionType = {
  AddSimilaritySegment: 'add-similarity-segment',
  EditSimilaritySegment: 'edit-similarity-segment',
  AddSeedSegment: 'add-seed-segment',
  RemoveSeedSegment: 'remove-seed-segment',
  RemoveDefinition: 'remove-definition',
  RemoveAll: 'remove-all',
} as const;
export type ActionType = ConstValues<typeof ActionType>;

export type LookalikeSegmentsAction =
  | {
      type: typeof ActionType.AddSimilaritySegment;
      definitionId: string;
      segment: LookalikeFormSegment;
      similarityLevel: SimilarityLevelValue;
    }
  | {
      type: typeof ActionType.EditSimilaritySegment;
      definitionId: string;
      segment: LookalikeFormSegment;
      similarityLevel: SimilarityLevelValue;
    }
  | {
      type: typeof ActionType.AddSeedSegment;
      definitionId: string;
      segment: LookalikeFormSegment;
    }
  | {
      type: typeof ActionType.RemoveSeedSegment;
      definitionId: string;
    }
  | {
      type: typeof ActionType.RemoveDefinition;
      definitionId: string;
    }
  | {
      type: typeof ActionType.RemoveAll;
    };

export const lookalikeSegmentsReducer = (
  state: LookalikeSegmentsState,
  action: LookalikeSegmentsAction
): LookalikeSegmentsState => {
  switch (action.type) {
    case ActionType.AddSimilaritySegment: {
      const existingDefinition = state.segmentsByDefinition[action.definitionId];
      const isNewDefinition = !existingDefinition;

      return {
        segmentsByDefinition: {
          ...state.segmentsByDefinition,
          [action.definitionId]: {
            ...existingDefinition,
            similaritySegment: action.segment,
          },
        },

        definitionOrder: isNewDefinition ? [...state.definitionOrder, action.definitionId] : state.definitionOrder,
      };
    }

    case ActionType.EditSimilaritySegment: {
      const existingDefinition = state.segmentsByDefinition[action.definitionId];

      if (!existingDefinition) {
        return state;
      }

      return {
        segmentsByDefinition: {
          ...state.segmentsByDefinition,
          [action.definitionId]: {
            ...existingDefinition,
            similaritySegment: action.segment,
          },
        },
        definitionOrder: state.definitionOrder,
      };
    }

    case ActionType.AddSeedSegment: {
      const existingDefinition = state.segmentsByDefinition[action.definitionId];

      if (!existingDefinition?.similaritySegment) {
        return state;
      }

      return {
        segmentsByDefinition: {
          ...state.segmentsByDefinition,
          [action.definitionId]: {
            ...existingDefinition,
            seedSegment: action.segment,
          },
        },
        definitionOrder: state.definitionOrder,
      };
    }

    case ActionType.RemoveSeedSegment: {
      const existingDefinition = state.segmentsByDefinition[action.definitionId];

      if (!existingDefinition) {
        return state;
      }

      return {
        segmentsByDefinition: {
          ...state.segmentsByDefinition,
          [action.definitionId]: {
            ...existingDefinition,
            seedSegment: undefined,
          },
        },
        definitionOrder: state.definitionOrder,
      };
    }

    case ActionType.RemoveDefinition: {
      const { [action.definitionId]: _, ...remaining } = state.segmentsByDefinition;

      return {
        segmentsByDefinition: remaining,
        definitionOrder: state.definitionOrder.filter(id => id !== action.definitionId),
      };
    }

    case ActionType.RemoveAll:
      return {
        segmentsByDefinition: {},
        definitionOrder: [],
      };

    default:
      return state;
  }
};
